/**
 * Bloom tier maths (pure, allocation-free; tested). The bloom chain has up to BLOOM_LEVELS levels at 1/2 .. 1/128
 * of the frame. `full` starts at 1/2 (filtered from the full-res frame), `half` at 1/4, so the widest halo is the
 * same on High and Medium and only the crisp core differs (style guide §3.5).
 */
import { BLOOM_LEVELS, type BloomMode } from './data'

/** Number of chain levels a mode renders. */
export function bloomLevelCount(mode: BloomMode): number {
  return mode === 'full' ? BLOOM_LEVELS : mode === 'half' ? BLOOM_LEVELS - 1 : 0
}

/** Frame-size divisor of chain level `k` (0 = finest) for a mode. */
export function bloomLevelDivisor(mode: BloomMode, k: number): number {
  return 2 ** (k + (mode === 'half' ? 2 : 1))
}

/** Size of chain level `k` for a drawing buffer dimension (never below 1). */
export function bloomLevelSize(mode: BloomMode, k: number, size: number): number {
  return Math.max(1, Math.floor(size / bloomLevelDivisor(mode, k)))
}

/**
 * Writes the weights of the rendered levels, finest first, into `out` and returns how many it wrote.
 * Weights mirror three's BloomNode: `w = mix(f, 1.2 - f, radius)`. `half` folds the 1/2 level's weight into
 * its finest (1/4) level so the total energy matches `full`.
 */
export function bloomLevelWeights(factors: readonly number[], radius: number, mode: BloomMode, out: Float32Array): number {
  const n = bloomLevelCount(mode)
  const r = Math.min(1, Math.max(0, radius))
  const skip = mode === 'half' ? 1 : 0
  for (let k = 0; k < n; k++) {
    const f = factors[k + skip]
    out[k] = f + (1.2 - 2 * f) * r
  }
  if (skip === 1 && n > 0) {
    const f0 = factors[0]
    out[0] += f0 + (1.2 - 2 * f0) * r
  }
  for (let k = n; k < out.length; k++) out[k] = 0
  return n
}
